import { useEffect, useState } from "react";
import MOButtonV2 from "../../../../components/commons/MOButtonV2";
import { CS_EVENT_TYPE_OPTIONS } from "../constants";
import {
  canSubmitPenaltyAction,
  isRichTextEmpty,
} from "../mapCtripEvents";
import StatusBadge from "./StatusBadge";
import ComplaintRichTextEditor from "./ComplaintRichTextEditor";
import "./complaintManagement.css";

const PenaltyAppealForm = ({ complaint, onSubmit, loading }) => {
  const defaultEventTypeId = complaint?.eventDetail?.eventTypeId
    ? String(complaint.eventDetail.eventTypeId)
    : "";

  const [action, setAction] = useState("appeal");
  const [eventTypeId, setEventTypeId] = useState(defaultEventTypeId);
  const [comments, setComments] = useState("");
  const [files, setFiles] = useState([]);
  const [editorKey, setEditorKey] = useState(0);

  const { penalty, appealHistory = [] } = complaint;
  const canSubmit = canSubmitPenaltyAction(complaint);

  useEffect(() => {
    setEventTypeId(defaultEventTypeId);
  }, [defaultEventTypeId]);

  const defaultOptionMissing =
    defaultEventTypeId &&
    !CS_EVENT_TYPE_OPTIONS.some(
      (item) => String(item.id) === defaultEventTypeId
    );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!eventTypeId) return;
    if (action === "appeal" && isRichTextEmpty(comments)) return;
    onSubmit({
      action,
      comments,
      attachments: files,
      eventTypeId: Number(eventTypeId),
    });
    setComments("");
    setFiles([]);
    setEditorKey((key) => key + 1);
  };

  return (
    <div className="cmp-penalty-section">
      <h4 className="cmp-section-title">Penalty Information</h4>
      <div className="cmp-info-grid">
        <InfoRow label="Reason" value={penalty?.reason} />
        <InfoRow label="Penalty" value={penalty?.amount} />
        <InfoRow label="Appeal ID" value={penalty?.appealId} />
        <InfoRow label="Punish ID" value={penalty?.punishId} />
        <InfoRow
          label="Appealable"
          value={penalty?.appealable ? "YES" : "NO"}
        />
        <InfoRow
          label="Remaining Appeals"
          value={penalty?.remainingAppeals ?? 0}
        />
        <InfoRow label="Appeals Used" value={penalty?.appealTime ?? 0} />
        <InfoRow label="Appeal Status" value={penalty?.appealStatus || "-"} />
        <InfoRow label="Appeal Result" value={penalty?.appealResult || "-"} />
        <InfoRow
          label="Punish Created"
          value={penalty?.punishCreateTime || "-"}
        />
        <InfoRow
          label="Current Status"
          value={<StatusBadge status={penalty?.currentStatus} />}
        />
      </div>

      {penalty?.appealReplyContent && (
        <div className="cmp-filter-field">
          <label>Ctrip Response</label>
          <p className="cmp-complaint-text">{penalty.appealReplyContent}</p>
        </div>
      )}

      {penalty?.hasPendingAppeal && (
        <p className="cmp-empty-text">Appeal submitted — waiting for Ctrip review.</p>
      )}

      {appealHistory.length > 0 && (
        <>
          <h4 className="cmp-section-title">Appeal History</h4>
          <div className="cmp-appeal-history">
            {appealHistory.map((appeal) => (
              <div key={appeal.id} className="cmp-appeal-item">
                <div>
                  <span className="cmp-appeal-label">{appeal.label}</span>
                  {appeal.content && (
                    <p className="cmp-timeline-message m-0 mt-4">
                      {appeal.content}
                    </p>
                  )}
                  {appeal.response && (
                    <p className="cmp-timeline-message m-0 mt-4">
                      <strong>Ctrip:</strong> {appeal.response}
                    </p>
                  )}
                </div>
                <span
                  className={`cmp-appeal-status cmp-appeal-${appeal.status.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {appeal.status}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {canSubmit && (
        <form className="cmp-reply-form" onSubmit={handleSubmit}>
          <h4 className="cmp-section-title">Appeal Action</h4>
          <div className="cmp-radio-group">
            <label className="cmp-radio">
              <input
                type="radio"
                name="penaltyAction"
                value="accept"
                checked={action === "accept"}
                onChange={() => setAction("accept")}
              />
              Accept Penalty
            </label>
            <label className="cmp-radio">
              <input
                type="radio"
                name="penaltyAction"
                value="appeal"
                checked={action === "appeal"}
                onChange={() => setAction("appeal")}
              />
              Appeal Penalty
            </label>
          </div>

          <div className="cmp-filter-field">
            <label>Event Type</label>
            <select
              value={eventTypeId}
              onChange={(e) => setEventTypeId(e.target.value)}
              required
            >
              <option value="">Select event type</option>
              {defaultOptionMissing && (
                <option value={defaultEventTypeId}>
                  {complaint.eventDetail?.eventTypeName ||
                    `Event Type ${defaultEventTypeId}`}
                </option>
              )}
              {CS_EVENT_TYPE_OPTIONS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name_en}
                </option>
              ))}
            </select>
          </div>

          {action === "appeal" && (
            <>
              <div className="cmp-filter-field">
                <label>Additional Comments</label>
                <ComplaintRichTextEditor
                  value={comments}
                  onChange={setComments}
                  placeholder="Provide additional details..."
                  editorKey={editorKey}
                  minHeight={140}
                />
              </div>
              <div className="cmp-file-upload">
                <label className="cmp-file-label">Upload Evidence</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) =>
                    setFiles(Array.from(e.target.files || []))
                  }
                />
                {files.length > 0 && (
                  <div className="cmp-file-list">
                    {files.map((f, i) => (
                      <span key={i} className="cmp-attachment-chip">
                        📎 {f.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <MOButtonV2
            text={action === "accept" ? "Accept Penalty" : "Submit Appeal"}
            type="submit"
            textColor="#ffffff"
            backgroundColor={action === "accept" ? "#EF4444" : "#29AEE6"}
            height={36}
            fontSize={13}
            padding="8px 28px"
            radius={4}
            loading={loading}
            disabled={
              !eventTypeId ||
              (action === "appeal" && isRichTextEmpty(comments)) ||
              loading
            }
          />
        </form>
      )}
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="cmp-info-row">
    <span className="cmp-info-label">{label}</span>
    <span className="cmp-info-value">{value || "-"}</span>
  </div>
);

export default PenaltyAppealForm;
